import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Eye, ImageOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SupplierWhtSetupStep } from "@/components/SupplierWhtSetupStep";
import { MaterialGstSetupStep } from "@/components/MaterialGstSetupStep";
import { InvoiceImageZoom } from "@/components/InvoiceImageZoom";
import { PdfAttachment } from "@/components/ThreeWayMatchPanel";
import { apiGet, apiGetBlob } from "@/utils/api";
import { goBackToRegister } from "@/utils/registerNav";

export default function TaxSetupInvoice() {
  const { worklistId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [vendorId, setVendorId] = useState(location.state?.vendorId || null);
  const [grnLines, setGrnLines] = useState(location.state?.grnLines || null);
  const [siteCode, setSiteCode] = useState(location.state?.siteCode || "");
  const [currency, setCurrency] = useState(location.state?.currency || "");
  const [buyerEntityName, setBuyerEntityName] = useState(location.state?.buyerEntityName || "");
  const [error, setError] = useState("");
  const [invoiceImageUrl, setInvoiceImageUrl] = useState(null);
  const [invoiceImageType, setInvoiceImageType] = useState(null);
  const [invoiceImageState, setInvoiceImageState] = useState("loading"); // loading | ready | unavailable
  const [invoiceCopyModalOpen, setInvoiceCopyModalOpen] = useState(false);

  useEffect(() => {
    if (vendorId && grnLines && currency) return;
    const hub = searchParams.get("hub") || "prod";
    apiGet(`/three-way-matches/context?${new URLSearchParams({ worklist_id: worklistId, hub })}`)
      .then((data) => {
        const resolvedVendor = data.worklist?.vendor_id || data.source?.vendor_id;
        if (!resolvedVendor) { setError("Could not determine the supplier for this receipt"); return; }
        setVendorId(resolvedVendor); setGrnLines(data.grn_lines || []); setSiteCode(data.source?.plant_code || ""); setBuyerEntityName(data.source?.buyer_entity_name || ""); setCurrency(data.source?.currency || "INR");
      })
      .catch((err) => setError(err.message));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worklistId, searchParams]);

  // Same scanned supplier-invoice copy NewInvoice.jsx shows on steps 2/3 —
  // surfaced here too so finance can cross-check the supplier's actual GST
  // rate on this page, before confirming tax settings below.
  useEffect(() => {
    if (!worklistId) return;
    let cancelled = false;
    let objectUrl = null;
    setInvoiceImageState("loading");
    setInvoiceImageUrl(null);
    const hub = searchParams.get("hub") || "prod";
    apiGetBlob(`/three-way-matches/source-attachment?${new URLSearchParams({ worklist_id: worklistId, hub })}`)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setInvoiceImageUrl(objectUrl);
        setInvoiceImageType(blob.type || "");
        setInvoiceImageState("ready");
      })
      .catch(() => { if (!cancelled) setInvoiceImageState("unavailable"); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [worklistId, searchParams]);

  const materials = useMemo(() => {
    if (!grnLines) return [];
    const seen = new Map();
    grnLines.filter((line) => !line.is_landed_cost_line).forEach((line) => {
      if (!seen.has(line.material_code)) seen.set(line.material_code, { material_code: line.material_code, material_description: line.material_description, hsn_code: line.hsn_code });
    });
    return Array.from(seen.values());
  }, [grnLines]);

  const goToInvoiceForm = () => navigate(`/invoices/new?${searchParams}`);

  return <main className="min-h-[calc(100vh-8rem)] bg-slate-50 px-4 py-6 sm:px-6 lg:px-10" data-testid="tax-setup-page">
    <div className="mx-auto max-w-6xl">
      <header className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 pb-5" data-testid="tax-setup-header">
        <div><p className="font-mono text-xs font-semibold uppercase text-[#076D74]">Pre-invoice tax check</p><h1 className="mt-2 text-3xl font-heading font-semibold text-slate-950">Confirm India tax settings</h1><p className="mt-2 text-sm text-slate-600">One-time per supplier/material — already-set values just need a quick confirm.</p>{siteCode && <p className="mt-2 text-xs text-slate-500">Site <span className="font-mono font-semibold text-slate-800" data-testid="tax-setup-site-id">{siteCode}</span>{buyerEntityName && <span className="text-slate-400"> ({buyerEntityName})</span>}</p>}</div>
        <div className="flex shrink-0 items-center gap-2">
          {invoiceImageState === "ready" && (
            <Button type="button" variant="outline" size="sm" className="h-9 gap-1.5 text-[13px]" onClick={() => setInvoiceCopyModalOpen(true)} data-testid="tax-setup-view-invoice-copy-button">
              <Eye size={14} /> View invoice copy
            </Button>
          )}
          {invoiceImageState === "loading" && (
            <span className="flex items-center gap-1.5 text-[11px] text-slate-400" data-testid="tax-setup-view-invoice-copy-loading"><Loader2 size={13} className="animate-spin" />Loading invoice copy…</span>
          )}
          {invoiceImageState === "unavailable" && (
            <span className="flex items-center gap-1 text-xs text-slate-500" data-testid="tax-setup-view-invoice-copy-unavailable"><ImageOff size={13} />No invoice copy from Materials Hub yet</span>
          )}
          <Button variant="outline" onClick={() => goBackToRegister(navigate)} data-testid="tax-setup-back-button"><ArrowLeft size={15} /> Back to register</Button>
        </div>
      </header>

      {error && <section role="alert" className="mt-6 border-l-2 border-red-600 bg-red-50 p-4 text-sm text-red-900" data-testid="tax-setup-error">{error}</section>}
      {!error && !vendorId && <div className="mt-6 flex min-h-52 items-center justify-center border border-slate-200 bg-white" data-testid="tax-setup-loading"><Loader2 size={20} className="animate-spin text-[#076D74]" /><span className="ml-2 text-sm text-slate-600">Loading receipt details…</span></div>}

      {vendorId && step === 1 && <div className="mt-6"><SupplierWhtSetupStep vendorId={vendorId} siteCode={siteCode} currency={currency} onContinue={() => setStep(2)} /></div>}
      {vendorId && step === 2 && <div className="mt-6"><MaterialGstSetupStep materials={materials} onContinue={goToInvoiceForm} onBack={() => setStep(1)} /></div>}

      <Dialog open={invoiceCopyModalOpen} onOpenChange={setInvoiceCopyModalOpen}>
        <DialogContent className="max-w-3xl h-[85vh] flex flex-col" data-testid="tax-setup-invoice-copy-modal">
          <DialogHeader>
            <DialogTitle className="pr-8 truncate">Supplier invoice copy</DialogTitle>
            <DialogDescription>Scanned supplier invoice fetched from Materials Hub, shown here for comparison while confirming tax settings.</DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1">
            {invoiceImageType === "application/pdf"
              ? <PdfAttachment src={invoiceImageUrl} />
              : <InvoiceImageZoom src={invoiceImageUrl} alt="Supplier invoice" />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  </main>;
}
